import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ItemCustomization = ({ item, onComplete, onClose }) => {
  const [customizations, setCustomizations] = useState({
    spiceLevel: item.spiceLevel || 2,
    sides: item.customizations?.sides?.[0] || '',
    sauce: item.customizations?.sauce?.[0] || '',
    extras: [],
    specialInstructions: ''
  });

  const [quantity, setQuantity] = useState(1);

  const handleCustomizationChange = (key, value) => {
    setCustomizations(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleExtraToggle = (extra) => {
    setCustomizations(prev => ({
      ...prev,
      extras: prev.extras.includes(extra)
        ? prev.extras.filter(e => e !== extra)
        : [...prev.extras, extra]
    }));
  };

  const calculatePrice = () => {
    let price = item.price * quantity;
    customizations.extras.forEach(extra => {
      if (extra.includes('+$')) {
        price += parseFloat(extra.match(/\+\$(\d+(?:\.\d{2})?)/)[1]) * quantity;
      }
    });
    return price;
  };

  const handleAddToCart = () => {
    onComplete(item, { ...customizations, quantity });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-background rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-background border-b border-gray-200 p-6 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 rounded-lg overflow-hidden">
              <Image
                src={item.image}
                alt={item.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h2 className="font-headline text-xl font-bold text-text-primary">{item.name}</h2>
              <p className="text-text-secondary">${item.price}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-organic"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Spice Level */}
          {item.customizations?.spiceLevel && (
            <div>
              <h3 className="font-semibold text-text-primary mb-3">Spice Level</h3>
              <div className="flex space-x-3">
                {item.customizations.spiceLevel.map((level) => (
                  <button
                    key={level}
                    onClick={() => handleCustomizationChange('spiceLevel', level)}
                    className={`flex-1 p-3 rounded-lg border-2 transition-organic ${
                      customizations.spiceLevel === level
                        ? 'border-primary bg-primary-50 text-primary' :'border-gray-200 hover:border-primary-200'
                    }`}
                  >
                    <div className="text-center">
                      <div className="flex justify-center space-x-1 mb-1">
                        {[1, 2, 3, 4, 5].map((dot) => (
                          <div
                            key={dot}
                            className={`w-2 h-2 rounded-full ${
                              dot <= level ? 'bg-warning' : 'bg-gray-200'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-sm font-medium">
                        {level === 1 ? 'Mild' : level === 2 ? 'Medium' : level === 3 ? 'Spicy' : level === 4 ? 'Hot' : 'Extra Hot'}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Sides */}
          {item.customizations?.sides && (
            <div>
              <h3 className="font-semibold text-text-primary mb-3">Choose Your Side</h3>
              <div className="grid grid-cols-2 gap-3">
                {item.customizations.sides.map((side) => (
                  <button
                    key={side}
                    onClick={() => handleCustomizationChange('sides', side)}
                    className={`p-3 rounded-lg border-2 transition-organic ${
                      customizations.sides === side
                        ? 'border-primary bg-primary-50 text-primary' :'border-gray-200 hover:border-primary-200'
                    }`}
                  >
                    {side}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Sauce */}
          {item.customizations?.sauce && (
            <div>
              <h3 className="font-semibold text-text-primary mb-3">Choose Your Sauce</h3>
              <div className="grid grid-cols-2 gap-3">
                {item.customizations.sauce.map((sauce) => (
                  <button
                    key={sauce}
                    onClick={() => handleCustomizationChange('sauce', sauce)}
                    className={`p-3 rounded-lg border-2 transition-organic ${
                      customizations.sauce === sauce
                        ? 'border-primary bg-primary-50 text-primary' :'border-gray-200 hover:border-primary-200'
                    }`}
                  >
                    {sauce}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Extras */}
          {item.customizations?.extras && (
            <div>
              <h3 className="font-semibold text-text-primary mb-3">Add Extras</h3>
              <div className="space-y-2">
                {item.customizations.extras.map((extra) => (
                  <label
                    key={extra}
                    className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer"
                  >
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={customizations.extras.includes(extra)}
                        onChange={() => handleExtraToggle(extra)}
                        className="w-5 h-5 text-primary rounded focus:ring-primary"
                      />
                      <span className="text-text-primary">{extra.split(' (+')[0]}</span>
                    </div>
                    {extra.includes('+$') && (
                      <span className="text-primary font-semibold">
                        +${extra.match(/\+\$(\d+(?:\.\d{2})?)/)[1]}
                      </span>
                    )}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Special Instructions */}
          <div>
            <h3 className="font-semibold text-text-primary mb-3">Special Instructions</h3>
            <textarea
              value={customizations.specialInstructions}
              onChange={(e) => handleCustomizationChange('specialInstructions', e.target.value)}
              placeholder="Any dietary restrictions or special requests?"
              className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              rows={3}
            />
          </div>

          {/* Quantity & Add to Cart */}
          <div className="flex items-center justify-between pt-6 border-t border-gray-200">
            <div className="flex items-center space-x-4">
              <span className="font-semibold text-text-primary">Quantity:</span>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-organic"
                >
                  <Icon name="Minus" size={16} />
                </button>
                <span className="font-semibold text-lg w-8 text-center">{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-organic"
                >
                  <Icon name="Plus" size={16} />
                </button>
              </div>
            </div>
            <button
              onClick={handleAddToCart}
              className="bg-primary text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-600 transition-organic flex items-center space-x-2"
            >
              <span>Add to Cart</span>
              <span className="font-bold">${calculatePrice().toFixed(2)}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemCustomization;